// Per-member identity colors: ColorBrewer Dark2 (qualitative, 8 classes).
// Assigned by seniority so a member always keeps the same color; only cycles
// when a household has more members than colors.
const MEMBER_HEX = ['#1B9E77', '#D95F02', '#7570B3', '#E7298A', '#66A61E', '#E6AB02', '#A6761D', '#666666'];

export const sortMembrosBySeniority = (membros) => [...membros].sort((a, b) => {
  if (a.criado_em && b.criado_em) return new Date(a.criado_em) - new Date(b.criado_em);
  return a.id - b.id;
});

export const getMemberHex = (membroId, membros) => {
  if (!membroId || !membros?.length) return MEMBER_HEX[0];
  const index = sortMembrosBySeniority(membros).findIndex(m => m.id === membroId);
  return MEMBER_HEX[(index >= 0 ? index : 0) % MEMBER_HEX.length];
};

// Category chips by id: light ColorBrewer tint with the darker step for the icon.
const CATEGORY_TONES = {
  1: 'bg-lavender-100 text-lavender-600',
  2: 'bg-peach-100 text-peach-600',
  3: 'bg-verde-100 text-verde-600',
  4: 'bg-azul-100 text-azul-600',
  5: 'bg-amarelo-100 text-amarelo-600',
};

export const getCategoryColor = (categoriaId) => CATEGORY_TONES[categoriaId] || CATEGORY_TONES[1];

// The five built-in categories are stored in Portuguese; translate them by id
// and fall back to the stored name for anything else.
export const getCategoryName = (categoria, t) => {
  if (!categoria) return '—';
  const key = `categories.${categoria.id}`;
  const translated = t(key);
  return translated === key ? categoria.nome : translated;
};

export const getInitials = (nome) => (nome || '')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map(p => p[0] || '')
  .join('')
  .toUpperCase() || '?';

const pad2 = (n) => String(n).padStart(2, '0');

export const getLocalDateISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// ISO timestamp -> value for a <input type="date"> in the local timezone
export const isoToDateInput = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// new Date('YYYY-MM-DD') parses as UTC midnight, which is the previous day in
// the Americas. Build the date from its parts so it lands on the local day.
export const parseDateInput = (value, endOfDay = false) => {
  const [y, m, d] = value.split('-').map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
};

export const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const [y, m, d] = dataNascimento.split('-').map(Number);
  const hoje = new Date();
  let idade = hoje.getFullYear() - y;
  if (hoje.getMonth() + 1 < m || (hoje.getMonth() + 1 === m && hoje.getDate() < d)) idade -= 1;
  return idade;
};

export const formatarMinutos = (minutos) => {
  const total = Math.round(minutos || 0);
  const horas = Math.floor(total / 60);
  const mins = total % 60;
  if (horas === 0) return `${mins}min`;
  if (mins === 0) return `${horas}h`;
  return `${horas}h ${mins}min`;
};

export const sortRegistros = (registros) => [...registros].sort((a, b) => {
  const diff = new Date(b.criado_em) - new Date(a.criado_em);
  return diff !== 0 ? diff : b.id - a.id;
});

// Supabase resolves with { error } instead of throwing; network failures can
// still reject. Normalise both so callers only have to check `error`.
export const exec = async (query) => {
  try {
    return await query;
  } catch (error) {
    return { data: null, error };
  }
};

// The API caps every response (1000 rows by default), so page until the
// reported total is reached. `buildQuery(from, to)` must select with
// { count: 'exact' } and a stable order.
export const fetchAllRows = async (buildQuery, pageSize = 1000) => {
  const rows = [];
  for (;;) {
    const { data, error, count } = await exec(buildQuery(rows.length, rows.length + pageSize - 1));
    if (error) return { data: null, error };
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (count === null || count === undefined || rows.length >= count) break;
  }
  return { data: rows, error: null };
};

export const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};
